"use client"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react"
import { motion } from "framer-motion"
import { useAuth } from '../../../contexts/AuthContext'

interface LoginProps {
  onLoginSuccess: () => void
}

export default function ModernLogin({ onLoginSuccess }: LoginProps) {
  const { t } = useTranslation()
  const { login, sessionExpired } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [focusedField, setFocusedField] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      await login(email, password)
      onLoginSuccess()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (err?.response?.status === 401) {
        setError(t('auth.errors.invalidCredentials'))
      } else if (typeof detail === 'string' && detail) {
        setError(detail)
      } else if (err?.response) {
        setError(t('auth.errors.generic'))
      } else {
        setError(t('auth.errors.serverUnreachable'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="flex items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          {/* Logo and Title */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center justify-center mb-4">
              <img src="/logo.svg" alt={t('layout.logoAlt')} className="h-16 w-auto dark:invert" />
            </div>
            <h1 className="text-4xl font-display font-bold text-gray-900 dark:text-gray-100 mb-2">{t('app.name')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('auth.tagline')}</p>
          </motion.div>

          {/* Login Form */}
          <motion.form
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-xl border border-gray-200 shadow-lg p-8 dark:bg-gray-900 dark:border-gray-800"
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
              {t('auth.welcomeBack')}
            </h2>

            {/* Email Field */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" htmlFor="email">
                {t('auth.emailLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`h-5 w-5 ${focusedField === 'email' ? 'text-primary-500' : 'text-gray-400'} transition-colors`} />
                </div>
                <input
                  className="input-modern pl-10 pr-4"
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" htmlFor="password">
                {t('auth.passwordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${focusedField === 'password' ? 'text-primary-500' : 'text-gray-400'} transition-colors`} />
                </div>
                <input
                  className="input-modern pl-10 pr-12"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  )}
                </button>
              </div>
            </div>

            {/* Session expired notice */}
            {sessionExpired && !error && (
              <div className="mb-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  {t('auth.sessionExpiredNotice')}
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
              >
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  {error}
                </p>
              </motion.div>
            )}

            {/* Additional Options */}
            <div className="flex items-center justify-between mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{t('auth.rememberMe')}</span>
              </label>
              <button type="button" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
                {t('auth.forgotPassword')}
              </button>
            </div>

            {/* Submit Button */}
            <button
              className="w-full btn-accent group"
              type="submit"
              disabled={isLoading}
            >
              <span className="flex items-center justify-center">
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('auth.signingIn')}
                  </>
                ) : (
                  <>
                    {t('auth.signIn')}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </span>
            </button>
          </motion.form>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center mt-6"
          >
            <p className="text-sm text-gray-400">
              {t('auth.footer')}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              <a
                href={t('layout.websiteUrl')}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 hover:underline underline-offset-2"
              >
                {t('layout.website')}
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
